"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<string, string>;
    designed: string;
    balanced: string;
    overTime: string;
    drifts: string;
    afterRebalance: string;
    backToDesign: string;
    marketMove: string;
    rebalance: string;
    annualSwing: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    assets: { equity: "株式", bond: "債券", reit: "REIT", cash: "預金" },
    designed: "設計時の配分",
    balanced: "バランスが取れている",
    overTime: "時間が経つと…",
    drifts: "株式が伸びて配分がずれる",
    afterRebalance: "リバランス後",
    backToDesign: "設計どおりに戻す",
    marketMove: "相場変動",
    rebalance: "リバランス",
    annualSwing: "値動きの幅（年）",
    title: "配分は時間とともにずれる → リバランスで戻す",
    description:
      "株式が上昇すると配分が自然にずれます。リバランスは相場予想ではなく、当初の設計に戻す作業です。",
  },
  en: {
    assets: { equity: "Equities", bond: "Bonds", reit: "REIT", cash: "Deposits" },
    designed: "The allocation as designed",
    balanced: "In balance",
    overTime: "As time passes…",
    drifts: "Equities grow and the allocation drifts",
    afterRebalance: "After rebalancing",
    backToDesign: "Back to the design",
    marketMove: "Market moves",
    rebalance: "Rebalance",
    annualSwing: "Range of movement (annual)",
    title: "Allocations drift over time → rebalancing brings them back",
    description:
      "When equities rise, the allocation drifts naturally. Rebalancing is not a market call — it returns you to the original design.",
  },
};

// ── Colors (same asset palette as persona baskets) ─────────────────

const ASSET_COLOR = {
  equity: COLOR.sky,
  bond: COLOR.amber,
  reit: "#ec4899",
  cash: "#71717a",
};

type AssetKey = keyof typeof ASSET_COLOR;

type Allocation = { key: AssetKey; pct: number };

// ── Three states ───────────────────────────────────────────────────

const TARGET: Allocation[] = [
  { key: "equity", pct: 40 },
  { key: "bond", pct: 25 },
  { key: "reit", pct: 15 },
  { key: "cash", pct: 20 },
];

const DRIFTED: Allocation[] = [
  { key: "equity", pct: 55 },
  { key: "bond", pct: 18 },
  { key: "reit", pct: 12 },
  { key: "cash", pct: 15 },
];

const REBALANCED: Allocation[] = TARGET; // back to target

// ── Per-asset risk (std. dev, annualized, conceptual) ───────────────

const ASSET_RISK: Record<AssetKey, number> = {
  equity: 18,
  bond: 5,
  reit: 15,
  cash: 0,
};

function portfolioRisk(allocations: Allocation[]) {
  // 相関ゼロの前提での簡易計算
  const variance = allocations.reduce((sum, a) => {
    const w = a.pct / 100;
    const sigma = ASSET_RISK[a.key];
    return sum + (w * sigma) ** 2;
  }, 0);
  return Math.sqrt(variance);
}

const TARGET_RISK = portfolioRisk(TARGET);
const DRIFTED_RISK = portfolioRisk(DRIFTED);
const REBALANCED_RISK = portfolioRisk(REBALANCED);

// ── Layout ─────────────────────────────────────────────────────────

const VW = 520;
const VH = 316;
const BAR_W = 100;
const BAR_H = 180;
const COL_GAP = 50;
const START_X = 45;
const BAR_Y = 72;

function colX(i: number) {
  return START_X + i * (BAR_W + COL_GAP);
}

// ── Stacked bar ────────────────────────────────────────────────────

function StackedBar({
  x,
  y,
  w,
  h,
  allocations,
  highlight,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  allocations: Allocation[];
  highlight?: AssetKey;
}) {
  const t = STRINGS[useDocLocale()];
  return (
    <g>
      {/* Border */}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={6}
        fill="none"
        stroke="currentColor"
        strokeWidth="0.8"
        strokeOpacity={0.12}
      />
      {allocations.map((a, i) => {
        const segH = (a.pct / 100) * h;
        const precedingPct = allocations.slice(0, i).reduce((sum, allocation) => sum + allocation.pct, 0);
        const segY = y + (precedingPct / 100) * h;
        const color = ASSET_COLOR[a.key];
        const isHighlighted = highlight === a.key;
        const isFirst = i === 0;
        const isLast = i === allocations.length - 1;

        // Rounded corners for first/last segment
        const rx = 6;
        const clipPath =
          isFirst && isLast
            ? `M ${x + rx} ${segY} H ${x + w - rx} Q ${x + w} ${segY} ${x + w} ${segY + rx} V ${segY + segH - rx} Q ${x + w} ${segY + segH} ${x + w - rx} ${segY + segH} H ${x + rx} Q ${x} ${segY + segH} ${x} ${segY + segH - rx} V ${segY + rx} Q ${x} ${segY} ${x + rx} ${segY} Z`
            : isFirst
              ? `M ${x + rx} ${segY} H ${x + w - rx} Q ${x + w} ${segY} ${x + w} ${segY + rx} V ${segY + segH} H ${x} V ${segY + rx} Q ${x} ${segY} ${x + rx} ${segY} Z`
              : isLast
                ? `M ${x} ${segY} H ${x + w} V ${segY + segH - rx} Q ${x + w} ${segY + segH} ${x + w - rx} ${segY + segH} H ${x + rx} Q ${x} ${segY + segH} ${x} ${segY + segH - rx} Z`
                : `M ${x} ${segY} H ${x + w} V ${segY + segH} H ${x} Z`;

        const midY = segY + segH / 2;
        const labelSize = segH > 28 ? 9 : 7.5;
        const pctSize = segH > 28 ? 10 : 8;

        return (
          <g key={a.key}>
            <path d={clipPath} fill={color} fillOpacity={isHighlighted ? 0.25 : 0.14} />
            {/* Segment border */}
            {!isFirst && (
              <line x1={x + 1} y1={segY} x2={x + w - 1} y2={segY} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.08} />
            )}
            {/* Label + pct inside */}
            {segH > 18 && (
              <>
                <text x={x + w / 2} y={midY - 2} textAnchor="middle" dominantBaseline="central" fontSize={labelSize} fill={color} fontWeight="600">
                  {t.assets[a.key]}
                </text>
                <text x={x + w / 2} y={midY + labelSize + 1} textAnchor="middle" dominantBaseline="central" fontSize={pctSize} fill={color} fontWeight="700">
                  {a.pct}%
                </text>
              </>
            )}
            {/* Too small: label on the right */}
            {segH <= 18 && (
              <>
                <text x={x + w + 6} y={midY + 1} fontSize="7.5" fill={color} fontWeight="600" dominantBaseline="central">
                  {t.assets[a.key]} {a.pct}%
                </text>
              </>
            )}
          </g>
        );
      })}
    </g>
  );
}

// ── Arrow ──────────────────────────────────────────────────────────

function Arrow({ x1, x2, y, label, color }: { x1: number; x2: number; y: number; label: string; color: string }) {
  const midX = (x1 + x2) / 2;
  const tipSize = 5;
  return (
    <g>
      <line x1={x1} y1={y} x2={x2 - tipSize} y2={y} stroke={color} strokeWidth="1.5" />
      <path d={`M ${x2 - tipSize} ${y - tipSize} L ${x2} ${y} L ${x2 - tipSize} ${y + tipSize}`} fill="none" stroke={color} strokeWidth="1.5" />
      <text x={midX} y={y - 10} textAnchor="middle" fontSize="8.5" fill={color} fontWeight="600">
        {label}
      </text>
    </g>
  );
}

// ── Drift indicator ────────────────────────────────────────────────

function DriftIndicator({ x, y1, y2, label }: { x: number; y1: number; y2: number; label: string }) {
  return (
    <g>
      <line x1={x} y1={y1} x2={x} y2={y2} stroke={COLOR.rose} strokeWidth="1.2" strokeDasharray="3 3" />
      <text x={x + 4} y={(y1 + y2) / 2 + 3} fontSize="7.5" fill={COLOR.rose} fontWeight="600">
        {label}
      </text>
    </g>
  );
}

// ── Main illustration ──────────────────────────────────────────────

function RebalancingIllustration() {
  const t = STRINGS[useDocLocale()];
  const col0 = colX(0);
  const col1 = colX(1);
  const col2 = colX(2);
  const arrowY = BAR_Y + BAR_H / 2;

  // Drift lines: show equity grew from 40% → 55%
  const driftedEquityBottom = BAR_Y + (DRIFTED[0].pct / 100) * BAR_H;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-2xl">
      {/* Column headers */}
      <text x={col0 + BAR_W / 2} y={BAR_Y - 30} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.designed}
      </text>
      <text x={col0 + BAR_W / 2} y={BAR_Y - 16} textAnchor="middle" fontSize="8.5" fill="currentColor">
        {t.balanced}
      </text>

      <text x={col1 + BAR_W / 2} y={BAR_Y - 30} textAnchor="middle" fontSize="11" fill={COLOR.rose} fontWeight="700">
        {t.overTime}
      </text>
      <text x={col1 + BAR_W / 2} y={BAR_Y - 16} textAnchor="middle" fontSize="8.5" fill={COLOR.rose}>
        {t.drifts}
      </text>

      <text x={col2 + BAR_W / 2} y={BAR_Y - 30} textAnchor="middle" fontSize="11" fill={COLOR.emerald} fontWeight="700">
        {t.afterRebalance}
      </text>
      <text x={col2 + BAR_W / 2} y={BAR_Y - 16} textAnchor="middle" fontSize="8.5" fill={COLOR.emerald}>
        {t.backToDesign}
      </text>

      {/* Stacked bars */}
      <StackedBar x={col0} y={BAR_Y} w={BAR_W} h={BAR_H} allocations={TARGET} />
      <StackedBar x={col1} y={BAR_Y} w={BAR_W} h={BAR_H} allocations={DRIFTED} highlight="equity" />
      <StackedBar x={col2} y={BAR_Y} w={BAR_W} h={BAR_H} allocations={REBALANCED} />

      {/* Arrows */}
      <Arrow x1={col0 + BAR_W + 8} x2={col1 - 8} y={arrowY} label={t.marketMove} color="currentColor" />
      <Arrow x1={col1 + BAR_W + 8} x2={col2 - 8} y={arrowY} label={t.rebalance} color={COLOR.emerald} />

      {/* Drift indicator between col0 and col1 for equity */}
      <DriftIndicator
        x={col1 + BAR_W + 4}
        y1={BAR_Y}
        y2={driftedEquityBottom}
        label="+15%"
      />

      {/* Risk metrics under each bar */}
      {(() => {
        const metricY = BAR_Y + BAR_H + 18;
        const valueY = metricY + 16;
        const deltaY = valueY + 13;
        const cols = [
          { x: col0, risk: TARGET_RISK, tone: "currentColor", delta: null as string | null },
          { x: col1, risk: DRIFTED_RISK, tone: COLOR.rose, delta: `+${(DRIFTED_RISK - TARGET_RISK).toFixed(1)}pt ↑` },
          { x: col2, risk: REBALANCED_RISK, tone: COLOR.emerald, delta: null },
        ];
        return cols.map((c, i) => (
          <g key={i}>
            <text x={c.x + BAR_W / 2} y={metricY} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.6}>
              {t.annualSwing}
            </text>
            <text x={c.x + BAR_W / 2} y={valueY} textAnchor="middle" fontSize="14" fill={c.tone} fontWeight="800">
              ±{c.risk.toFixed(1)}%
            </text>
            {c.delta && (
              <text x={c.x + BAR_W / 2} y={deltaY} textAnchor="middle" fontSize="8" fill={c.tone} fontWeight="700">
                {c.delta}
              </text>
            )}
          </g>
        ));
      })()}
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocRebalancingDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <RebalancingIllustration />
    </IllustrationCard>
  );
}
